import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  CloudRain, CloudLightning, Wind, Snowflake, Thermometer, Wheat, Package, Wrench,
  TrendingUp, TrendingDown, Newspaper, AlertTriangle, ChevronRight, ShieldCheck,
} from "lucide-react";

const pick = (lang, hi, en) => (lang === "English" ? en : hi);
const ICONS = {
  "cloud-rain": CloudRain, "cloud-lightning": CloudLightning, wind: Wind, snowflake: Snowflake,
  thermometer: Thermometer, wheat: Wheat, package: Package, wrench: Wrench,
  "trending-up": TrendingUp, "trending-down": TrendingDown, news: Newspaper, alert: AlertTriangle,
};
const SEV = {
  red: { c: "#e11d48", bg: "rgba(225,29,72,0.08)", ring: "rgba(225,29,72,0.25)" },
  amber: { c: "#d97706", bg: "rgba(217,119,6,0.08)", ring: "rgba(217,119,6,0.22)" },
  blue: { c: "#2563eb", bg: "rgba(37,99,235,0.07)", ring: "rgba(37,99,235,0.2)" },
};

function AlertCard({ a, language }) {
  const Icon = ICONS[a.icon] || AlertTriangle;
  const s = SEV[a.severity] || SEV.blue;
  const title = pick(language, a.title_hi, a.title_en);
  const when = pick(language, a.when_hi, a.when_en);
  const action = pick(language, a.action_hi, a.action_en);
  const Wrapper = a.url ? "a" : "div";
  const wrapProps = a.url ? { href: a.url, target: "_blank", rel: "noreferrer" } : {};
  return (
    <motion.div
      initial={a.severity === "red" ? { boxShadow: `0 0 0 0 ${s.ring}` } : false}
      animate={a.severity === "red" ? { boxShadow: [`0 0 0 0 ${s.ring}`, `0 0 0 6px rgba(225,29,72,0)`] } : {}}
      transition={a.severity === "red" ? { repeat: Infinity, duration: 2.2 } : {}}>
      <Wrapper {...wrapProps}
        className="block rounded-2xl p-3.5 border tap active:scale-[0.99] transition-transform" style={{ background: s.bg, borderColor: s.ring }}
        data-testid={`warning-${a.severity}`}>
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${s.c}1f` }}>
            <Icon style={{ color: s.c, height: 18, width: 18 }} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[13.5px] font-bold text-[#1c2b1e] leading-snug">{title}</p>
              {when && <span className="text-[10px] font-semibold rounded-full px-2 py-0.5" style={{ background: `${s.c}1f`, color: s.c }}>⏱ {when}</span>}
            </div>
            <p className="text-[12px] text-[#55634f] mt-1 leading-relaxed">{action}</p>
            <p className="text-[10px] text-[#9aa79a] mt-1">{pick(language, "स्रोत", "Source")}: {a.source}</p>
          </div>
        </div>
      </Wrapper>
    </motion.div>
  );
}

export function AlertsRail({ warnings, language, item }) {
  const alerts = warnings?.alerts || [];
  const counts = warnings?.counts || { red: 0, amber: 0, blue: 0 };
  return (
    <motion.div variants={item} className="agro-glass rounded-[22px] p-5" data-testid="alerts-rail">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display font-bold text-[18px] text-[#173f1e] flex items-center gap-1.5">
          <AlertTriangle className="h-5 w-5 text-[#e11d48]" />{pick(language, "समाचार और चेतावनियां", "Alerts & News")}
        </h2>
        {counts.red > 0 && (
          <motion.span animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 1.8 }}
            className="text-[11px] font-bold text-white bg-rose-600 rounded-full px-2.5 py-0.5" data-testid="red-count">
            🔴 {counts.red} {pick(language, "ज़रूरी", "urgent")}
          </motion.span>
        )}
      </div>
      <p className="text-[12px] text-[#6c7a67] mb-3">
        {pick(language, "आपके खेत के लिए आगे की चेतावनियां — पहले से तैयार रहें 🌾", "Advance warnings for your farm — stay a step ahead 🌾")}
      </p>

      {alerts.length === 0 ? (
        <div className="flex flex-col items-center text-center py-8" data-testid="alerts-empty">
          <ShieldCheck className="h-10 w-10 text-[#2f9e3a] mb-2" />
          <p className="text-sm font-semibold text-[#173f1e]">{pick(language, "सब कुछ ठीक है!", "All clear!")}</p>
          <p className="text-[12px] text-[#6c7a67] mt-0.5">{pick(language, "अभी कोई चेतावनी नहीं। खेती का आनंद लें 🌿", "No warnings right now. Happy farming 🌿")}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {alerts.slice(0, 8).map((a, i) => <AlertCard key={i} a={a} language={language} />)}
        </div>
      )}

      <Link to="/weather" className="mt-3 flex items-center justify-center gap-1 text-sm font-semibold text-[#2f7d33] tap" data-testid="alerts-more">
        {pick(language, "पूरा मौसम व सलाह देखें", "See full weather & advice")} <ChevronRight className="h-4 w-4" />
      </Link>
    </motion.div>
  );
}
